import { useCallback, useEffect, useRef, useState } from "react";

import { useSalesTransactionsDataLoaderContext } from "./SalesTransactionsDataLoaderContext";

export type ReviewQueueKind = "sales" | "transactions";

interface ReviewQueueLoadState {
  loading: boolean;
  error: unknown;
}

interface ReviewQueueLoaderResult extends ReviewQueueLoadState {
  retry: () => void;
}

/**
 * Loads the unbounded review queues on demand (feature flag
 * `deferUnreviewedLoad`). The promise resolves on the first Firestore snapshot,
 * so dialogs can show a local spinner instead of a false empty state.
 */
export function useReviewQueueLoader(
  open: boolean,
  kind: ReviewQueueKind
): ReviewQueueLoaderResult {
  const { getManager } = useSalesTransactionsDataLoaderContext();
  // The provider currently memoizes getManager, but keep it in a ref so a
  // future identity change cannot re-run the load effect.
  const getManagerRef = useRef(getManager);
  getManagerRef.current = getManager;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<ReviewQueueLoadState>({
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!open) {
      setState({ loading: false, error: null });
      return;
    }

    const manager = getManagerRef.current();
    if (!manager) return;

    let cancelled = false;
    setState({ loading: true, error: null });

    const promise =
      kind === "sales"
        ? manager.ensureUnreviewedSalesLoaded()
        : manager.ensureUnreviewedTransactionsLoaded();

    promise
      .then(() => {
        if (!cancelled) setState({ loading: false, error: null });
      })
      .catch((error) => {
        console.error(`Failed to load unreviewed ${kind}`, error);
        if (!cancelled) setState({ loading: false, error });
      });

    return () => {
      cancelled = true;
    };
  }, [open, kind, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  return { ...state, retry };
}
